import type { ChatAttachment, ChatMessage } from '../models/chat.model'

/**
 * Meeting-chat transport: the Phoenix room channel, and nothing else.
 *
 * This replaces the in-memory mock that used to live here (its own comment
 * said "when the real socket layer lands, replace this with a socket adapter" —
 * this is that adapter). Messages ride the same channel the meeting already
 * holds for signaling, so chat needs no second connection, no SDK session
 * token, and no sdk-server round trip.
 *
 * Explicitly NOT the messenger path: `features/messenger` talks to sdk-server
 * over its own socket for org/tenant conversations. Meeting chat shares no
 * transport, no state and no identity with it.
 */

/** What the server puts on the wire — see `RoomChannel.handle_in("chat:message")`. */
export type WireChatMessage = {
  id: string
  from: string
  fromName: string | null
  to: string | null
  text: string
  attachment: ChatAttachment | null
  ts: number
}

export type ChatChannel = {
  sendEvent: (event: string, payload: Record<string, unknown>) => void
  /**
   * Send, and wait for the server's answer.
   *
   * Optional because chat itself never needs it — a message that does not
   * arrive is visible as a message that did not arrive. It is here because the
   * room channel is one channel, and the moderator commands that ride it
   * (ending breakout rooms, moving somebody) are refusable and were being
   * refused silently.
   */
  request?: (
    event: string,
    payload: Record<string, unknown>,
  ) => Promise<{ status: string; response: Record<string, unknown> }>
  subscribe: (event: string, handler: (payload: unknown) => void) => () => void
}

export type ChatTransport = {
  /** Send a message. `to` null/undefined = the whole room. */
  publish: (input: {
    id: string
    text: string
    to?: string | null
    attachment?: ChatAttachment | null
  }) => void
  /** Announce (or withdraw) a typing indicator for one thread. */
  publishTyping: (typing: boolean, to?: string | null) => void
  /** Inbound messages, already normalized to the client's shape. */
  subscribe: (handler: (msg: ChatMessage) => void) => () => void
  /** Inbound typing indicators. */
  subscribeTyping: (
    handler: (t: { from: string; fromName: string; to: string | null; typing: boolean }) => void,
  ) => () => void
  // No `subscribePermissions` here. Host controls are not a chat concern: they
  // are room state, they cover nine things besides chat, and MeetingScreen
  // subscribes to `host:controls` directly into the meeting-host-controls store.
  // This transport had one that nothing ever called, listening for an event
  // that no longer exists.
}

/**
 * Normalize a wire message into the shape the panel renders.
 *
 * The sender's identity comes from the server, never from what the client
 * sent — `RoomChannel` stamps `from`/`fromName` off the socket assigns, so a
 * participant cannot post as someone else.
 */
export function toChatMessage(wire: WireChatMessage): ChatMessage {
  return {
    id: wire.id,
    senderId: wire.from,
    senderName: wire.fromName || wire.from,
    text: wire.text ?? '',
    timestamp: wire.ts ?? Date.now(),
    type: 'active',
    isRead: false,
    ...(wire.attachment ? { attachment: wire.attachment } : {}),
    to: wire.to ?? null,
  }
}

export function createChannelTransport(channel: ChatChannel): ChatTransport {
  return {
    publish: ({ id, text, to, attachment }) => {
      channel.sendEvent('chat:message', {
        id,
        text,
        to: to ?? null,
        ...(attachment ? { attachment } : {}),
      })
    },

    publishTyping: (typing, to) => {
      channel.sendEvent('chat:typing', { typing, to: to ?? null })
    },

    subscribe: (handler) =>
      channel.subscribe('chat:message', (payload) => {
        const wire = payload as WireChatMessage
        if (!wire?.id || !wire?.from) return
        handler(toChatMessage(wire))
      }),

    subscribeTyping: (handler) =>
      channel.subscribe('chat:typing', (payload) => {
        const t = payload as {
          from?: string
          fromName?: string
          to?: string | null
          typing?: boolean
        }
        if (!t?.from) return
        handler({
          from: t.from,
          fromName: t.fromName || t.from,
          to: t.to ?? null,
          typing: t.typing === true,
        })
      }),

  }
}
